<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  nested: {
    type: Boolean,
    default: false,
  },
  expandable: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  missingLocales: {
    type: Array,
    default: () => [],
  },
  addingLocale: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle-expand', 'add-locale']);

const store = useStore();
const { t } = useI18n();
const toggling = ref(false);
const localeMenuOpen = ref(false);

const isEnabled = computed(() => !!props.widget.enabled);
const isPrimary = computed(() => props.widget.is_primary !== false);

const toggleEnabled = async () => {
  if (!isPrimary.value) return;
  toggling.value = true;
  try {
    await store.dispatch('bookingWidgets/update', {
      id: props.widget.id,
      enabled: !isEnabled.value,
    });
  } catch {
    useAlert(t('BOOKING_WIDGETS.SAVE_ERROR'));
  } finally {
    toggling.value = false;
  }
};

const pickLocale = locale => {
  localeMenuOpen.value = false;
  emit('add-locale', locale);
};
</script>

<template>
  <tr
    class="border-b border-n-weak"
    :class="[
      nested ? 'bg-n-solid-1' : '!border-t-2 !border-t-strong',
      expandable ? 'cursor-pointer select-none' : '',
    ]"
    @click="expandable ? emit('toggle-expand') : null"
  >
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <div
        class="flex min-w-0 flex-col"
        :class="nested ? 'pl-7' : ''"
      >
        <div class="flex min-w-0 items-center gap-2">
          <span
            v-if="!nested"
            class="flex size-5 shrink-0 items-center justify-center"
          >
            <svg
              v-if="expandable"
              class="size-3.5 transition-transform"
              :class="expanded ? 'rotate-90' : ''"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </span>
          <p
            class="truncate text-n-slate-12"
            :class="nested ? 'text-sm font-medium' : 'font-semibold'"
          >
            {{ widget.name }}
          </p>
          <span
            v-if="widget.locale"
            class="shrink-0 rounded bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-n-slate-11"
          >
            {{ widget.locale }}
          </span>
          <span
            v-if="nested"
            class="shrink-0 rounded bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
          >
            {{ $t('BOOKING_WIDGETS.LOCALE_COPY') }}
          </span>
        </div>
      </div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4" @click.stop>
      <Switch
        v-if="isPrimary"
        :model-value="isEnabled"
        :disabled="toggling"
        @change="toggleEnabled"
      />
      <span v-else class="text-xs text-n-slate-10">—</span>
    </td>
    <td class="py-4" @click.stop>
      <div class="flex items-center justify-end gap-1">
        <OnClickOutside
          v-if="isPrimary && missingLocales.length"
          @trigger="localeMenuOpen = false"
        >
          <div class="relative">
            <Button
              v-tooltip.top="$t('BOOKING_WIDGETS.ADD_LOCALE')"
              icon="i-lucide-languages"
              slate
              md
              faded
              :is-loading="addingLocale"
              @click="localeMenuOpen = !localeMenuOpen"
            />
            <div
              v-if="localeMenuOpen"
              class="absolute right-0 z-20 mt-1 min-w-[7rem] overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 py-1 shadow-lg"
            >
              <button
                v-for="locale in missingLocales"
                :key="locale"
                type="button"
                class="block w-full px-3 py-1.5 text-left text-xs font-semibold uppercase text-n-slate-12 hover:bg-n-alpha-1"
                @click="pickLocale(locale)"
              >
                {{ locale }}
              </button>
            </div>
          </div>
        </OnClickOutside>
        <Button
          v-tooltip.top="$t('BOOKING_WIDGETS.EDIT')"
          icon="i-lucide-pen-line"
          slate
          md
          faded
          :is-loading="loading"
          @click="$emit('edit', widget)"
        />
        <Button
          v-tooltip.top="$t('BOOKING_WIDGETS.DELETE')"
          :is-loading="loading"
          icon="i-lucide-trash-2"
          md
          ruby
          faded
          @click="$emit('delete', widget)"
        />
      </div>
    </td>
  </tr>
</template>
